/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLang } from '../hooks/useLang'
import { getRole, getTenantId, getToken } from '../utils/channelStorage'

const BASE = import.meta.env.VITE_API_BASE_URL
const LOCAL = import.meta.env.VITE_API_LOCAL_URL

// 访客与客服共享的大部分接口路径。少数在两种角色下语义/路径不同的接口，
// 单独枚举一份 AgentApi，由封装函数按 role 分流。
enum Api {
  GET_SETTING = '/merchant/channel/website',
  GET_HISTORY = '/merchant/chats/messages',
  GET_LAT_CHAT = '/merchant/chats/getLatestChat',
  TRANSFER_AGENT = '/chats/chat/clientRouteAgent',
  GET_NAVIGATION = '/merchant/chats/getQuickNavigation',
  GET_USER_INFO = '/merchant/chats/userInfo',

  // 访客侧的历史会话清单接口
  GET_USER_HISTORY = '/merchant/chats/history',
  // 客服侧的历史会话清单接口（与主 SPA 对齐）
  GET_CHAT_HISTORY = '/merchant/chats/chatHistory',

  SUBMIT_GRADE = '/chats/chat/handleChatGrade',
  SEND_GRADE = '/chats/chat/sendGrade',
  GET_GRADE = '/chats/chat/getGradeConfig',

  // matchQuestions "解决问题" 打点
  QUE_CLICKS = '/merchant/channel/queClicks',
}

// 通用的请求配置接口
interface RequestConfig {
  method: 'GET' | 'POST'
  endpoint: string
  channelId?: string
  body?: any
  urlParams?: string
}

function getLocalFromURL() {
  // 使用当前页面URL的查询参数
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('local') || 'false'
}

// 通用的请求函数
async function makeRequest(config: RequestConfig) {
  const { method, endpoint, channelId, body, urlParams } = config

  const localParam = getLocalFromURL()

  console.log('makeRequest', localParam)
  const URL = localParam === 'true' ? LOCAL : BASE

  const { getUCLang } = useLang()

  const token = getToken()
  let url = URL + endpoint
  if (urlParams) {
    url += urlParams
  }

  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
  }

  // Tenant-Id 的取值按当前身份走 getTenantId()：
  // - 访客：channelId（访客的租户边界）
  // - 客服：memberId（客服自己的租户边界，后端权限校验依赖）
  // 大小写：HTTP header 名协议层不敏感，此处使用 Tenant-Id 与后端约定一致。
  // 触发条件保留"channelId 存在时才注入 auth 头"的既有语义，避免为不需要鉴权的
  // 早期兼容路径带上空 Bearer；客服 case 下 channelId 由调用方保证已就位。
  if (channelId) {
    headers['Authorization'] = `Bearer ${token}`
    headers['Tenant-Id'] = getTenantId()
    headers['Accept-Language'] = getUCLang()
  }

  const requestOptions: RequestInit = {
    method,
    headers,
  }

  if (method === 'POST' && body) {
    requestOptions.body = JSON.stringify(body)
  }

  const response = await fetch(url, requestOptions)

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }

  return response
}

// 封装后的具体函数
export async function getStyleSetting(channelId?: string) {
  const urlParams = channelId ? `/${channelId}?_t=${new Date().getTime()}` : ''

  return makeRequest({
    method: 'GET',
    endpoint: Api.GET_SETTING,
    channelId,
    urlParams,
  })
}

export async function getChatHistory(chatId: string, sequenceId?: string, channelId?: string) {
  return makeRequest({
    method: 'POST',
    endpoint: Api.GET_HISTORY,
    channelId,
    body: { chatId, sequenceId },
  })
}

// 拉客户的历史会话清单。
// - 访客身份：走 /merchant/chats/history，参数为空（token 即身份）
// - 客服身份：走 /merchant/chats/chatHistory（与主 SPA 一致），必须显式带 userId
//   （客户的 userId，从 /merchant/chats/userInfo 或首页消息里的 senderType==='custom' 抠出）
export async function getUserHistory(
  channelId?: string,
  params?: { userId?: string; validFlag?: boolean },
) {
  const isAgent = getRole() === 'agent'
  const endpoint = isAgent ? Api.GET_CHAT_HISTORY : Api.GET_USER_HISTORY

  const query: string[] = []
  if (params?.userId) query.push(`userId=${encodeURIComponent(params.userId)}`)
  if (params?.validFlag !== undefined) query.push(`validFlag=${params.validFlag}`)
  const urlParams = query.length ? `?${query.join('&')}` : ''

  return makeRequest({
    method: 'GET',
    endpoint,
    channelId,
    urlParams,
  })
}

// 拉指定 chatId 的元信息：userId、channelId、channelName、accessCount 等。
// 客服进入 widget 时首个必须打的接口——同时兼作 token 校验：401 视为 token 无效。
// 响应结构：{ code, data: { chatId, userId, channelId, channelUserId, channelType, ... }, msg }
export async function getChatUserInfo(chatId: string, channelId?: string) {
  return makeRequest({
    method: 'GET',
    endpoint: Api.GET_USER_INFO,
    channelId,
    urlParams: `?chatId=${encodeURIComponent(chatId)}`,
  })
}

export async function getLatestChat(channelId?: string) {
  return makeRequest({
    method: 'GET',
    endpoint: Api.GET_LAT_CHAT,
    channelId,
  })
}

export async function submitChatGrade(
  channelId: string,
  params: {
    chatId: string
    grade: number
    comment: string
  },
) {
  return makeRequest({
    method: 'GET',
    endpoint: Api.SUBMIT_GRADE,
    channelId,
    urlParams: `?chatId=${params.chatId}&grade=${params.grade}&comment=${params.comment}`,
  })
}

export async function sendChatGrade(channelId: string, params: { chatId: string }) {
  return makeRequest({
    method: 'GET',
    endpoint: Api.SEND_GRADE,
    channelId,
    urlParams: `?chatId=${params.chatId}`,
  })
}

export async function getNavigation(channelId: string) {
  return makeRequest({
    method: 'GET',
    endpoint: Api.GET_NAVIGATION,
    channelId,
    urlParams: `?channelId=${encodeURIComponent(channelId)}`,
  })
}

export async function getGradeConfig(channelId?: string) {
  return makeRequest({
    method: 'GET',
    endpoint: Api.GET_GRADE,
    channelId,
  })
}

/** Routes the visitor to an agent independently of question messages and keyword matching. */
export async function transferAgent(channelId: string) {
  return makeRequest({ method: 'POST', endpoint: Api.TRANSFER_AGENT, channelId })
}

// matchQuestions 弹窗内 "解决问题" 按钮的打点接口。
// GET /merchant/channel/queClicks?messageId=xxx&queId=123
// queId 是 19 位雪花 ID，前端必须以字符串形式携带，否则 JS Number 精度会截断。
export async function queClicks(params: { messageId: string; queId: string }, channelId?: string) {
  const query =
    `?messageId=${encodeURIComponent(params.messageId)}` +
    `&queId=${encodeURIComponent(params.queId)}`
  return makeRequest({
    method: 'GET',
    endpoint: Api.QUE_CLICKS,
    channelId,
    urlParams: query,
  })
}
